import type { TableColumn } from './courseConfig'

// 列表类型
export type ListType = 'stockPool' | 'signalPool' | 'hotCases'

// 信号类型
export type SignalType = 'chipMaster' | 'mengzhuTrend' | string

// 列顺序存储数据结构
interface ColumnOrderData {
    columnKeys: string[]
    version: string
}

/**
 * 生成存储键
 * @param courseNo 课程编号
 * @param listType 列表类型
 * @param signalType 信号类型（可选）
 */
export function generateStorageKey(
    courseNo: string | number,
    listType: ListType,
    signalType?: SignalType
): string {
    const base = `smallAmount_columnOrder_${courseNo}_${listType}`
    return signalType ? `${base}_${signalType}` : base
}

/**
 * 保存列顺序到 localStorage
 * @param key 存储键
 * @param columnKeys 列键数组
 * @param version 配置版本
 */
export function saveColumnOrder(key: string, columnKeys: string[], version: string): void {
    try {
        const data: ColumnOrderData = { columnKeys, version }
        localStorage.setItem(key, JSON.stringify(data))
    } catch (e) {
        console.error('保存列顺序失败:', e)
    }
}

/**
 * 从 localStorage 获取列顺序
 * @param key 存储键
 */
export function getColumnOrder(key: string): ColumnOrderData | null {
    try {
        const data = localStorage.getItem(key)
        if (data) {
            return JSON.parse(data) as ColumnOrderData
        }
    } catch (e) {
        console.error('获取列顺序失败:', e)
    }
    return null
}


/**
 * 清除列顺序
 * @param key 存储键
 */
export function clearColumnOrder(key: string): void {
    try {
        localStorage.removeItem(key)
    } catch (e) {
        console.error('清除列顺序失败:', e)
    }
}

/**
 * 验证列配置是否兼容
 * @param savedKeys 保存的列键数组
 * @param currentColumns 当前列配置
 */
export function validateColumnConfig(savedKeys: string[], currentColumns: TableColumn[]): boolean {
    if (!savedKeys || savedKeys.length === 0) return false

    const currentKeys = new Set(currentColumns.map(col => col.key))
    // 检查保存的键是否都存在于当前配置中
    return savedKeys.every(key => currentKeys.has(key))
}

/**
 * 过滤可拖动列（排除固定列）
 * @param columns 列配置数组
 */
export function filterDraggableColumns(columns: TableColumn[]): TableColumn[] {
    return columns.filter(col => !col.fixed)
}

/**
 * 应用列顺序
 * @param columns 原始列配置
 * @param order 自定义顺序（列键数组）
 */
export function applyColumnOrder(columns: TableColumn[], order: string[]): TableColumn[] {
    if (!order || order.length === 0) return columns

    // 分离固定列和可拖动列
    const fixedLeftColumns = columns.filter(col => col.fixed === 'left')
    const fixedRightColumns = columns.filter(col => col.fixed === 'right')
    const draggableColumns = columns.filter(col => !col.fixed)

    // 按自定义顺序排列可拖动列
    const orderedDraggable: TableColumn[] = []
    const columnMap = new Map(draggableColumns.map(col => [col.key, col]))

    for (const key of order) {
        const col = columnMap.get(key)
        if (col) {
            orderedDraggable.push(col)
            columnMap.delete(key)
        }
    }

    // 添加剩余的列（新增的列）
    for (const col of columnMap.values()) {
        orderedDraggable.push(col)
    }

    // 合并：左固定列 + 排序后的可拖动列 + 右固定列
    return fixedLeftColumns.concat(orderedDraggable).concat(fixedRightColumns)
}

/**
 * 生成列配置版本号
 * @param columns 列配置数组
 */
export function generateColumnConfigVersion(columns: TableColumn[]): string {
    const keys = columns.map(col => col.key).sort().join(',')
    // 简单的哈希函数
    let hash = 0
    for (let i = 0; i < keys.length; i++) {
        const char = keys.charCodeAt(i)
        hash = ((hash << 5) - hash) + char
        hash = hash & hash // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36)
}
